import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Search,
  RefreshCw,
  Filter,
  Table,
  LayoutGrid,
  Settings,
  Calendar,
  X,
  Plus,
  Palette,
  ChevronDown,
  ChevronLeft,
  User,
  UserPlus,
  Mail,
  Phone,
  Download,
  FileText,
} from 'lucide-react';
import { PiExport, PiExportFill } from 'react-icons/pi';
import { TfiLayoutColumn3 } from "react-icons/tfi";
import DynamicCalendar from '../../../components/DynamicCalendar/DynamicCalendar';
import Loader from '../../../components/Loader/Loader';
import EnquiryCustomField from './EnquiryCustomField';

import EnquiryDetails from './EnquiryDetails';
import EnquiryTable from './components/EnquiryTable';
import Enquiryoperations from './components/Enquiryoperations';
import { LeadDetails } from './components/LeadDetails';
import AddEnquiryForm from './components/AddEnquiryForm';
import PDFExportOverlay from './components/PDFExportOverlay';
import CallWidget from '../../../components/common/CallWidget/CallWidget';
import PremiumModuleWrapper from '../../Task/Components/common/PremiumModuleWrapper';
import SubHeader from '../../../components/common/SubHeader/SubHeader';
import GridMainPanelLayout from '../../../components/common/Panels/GridMainPanelLayout';
import Panels from '../../../components/common/Panels/Panels';
import { getEnquiryList } from '../../../utils/enquiry';
import { getSession } from '../../../getSession';
import { Icon } from '@iconify/react';
// import nodata from '../../../assets/nodata.gif'; // User requested to use public/nodata.gif
import API_ENDPOINTS from '../../../config/apiEndpoints';
import EnquiryActionOperation from './EnquiryActionOperation';
import EnquiryMassoperation from './EnquiryMassoperation';
import Alert from '../../../common/Alerts/Alert';
import usePermissions from '../../Master_Settings/usePermissions';
import useFieldMasking from '../../Master_Settings/useFieldMasking';
import InternationalSMSForm from '../Forms/InternationalSMSForm';
import './EnquiriesModule.css';
import EnquiryImport from './EnquiryImport';
import EnquiryFilterSidebar from './components/EnquiryFilterSidebar';

const Enquiries = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlCustomSearchId = searchParams.get('CustomSearchId');
  const urlFilter = searchParams.get('filter');
  const urlSearchName = searchParams.get('SearchName');

  const reportAction = searchParams.get('EnquiryReport');
  const reportDate = searchParams.get('ReportDate');
  const sourceName = searchParams.get('SourceName');
  const mediumName = searchParams.get('MediumName');
  const campaignName = searchParams.get('CampaignName');
  const createdBy = searchParams.get('CreatedBy');
  const reportFromDate = searchParams.get('ReportFromDate');
  const reportToDate = searchParams.get('ReportToDate');

  const [showDuplicateConfirmation, setShowDuplicateConfirmation] = useState(false);
  const [showDuplicateSuccess, setShowDuplicateSuccess] = useState(false);
  const [hasDuplicates, setHasDuplicates] = useState(true); // Default to true as per request image

  const [selectedLead, setSelectedLead] = useState(0);
  const [totalRecord, setTotalRecord] = useState(0);
  const [enquiries, setEnquiries] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  const [showCustomFields, setShowCustomFields] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const { userId, TokenId, parentId } = getSession()
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const [showAddEnquiry, setShowAddEnquiry] = useState(false);
  const [showToolbarMenu, setShowToolbarMenu] = useState(false);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [selectedEnquiries, setSelectedEnquiries] = useState([]);
  const [isAllSelectedAcrossPages, setIsAllSelectedAcrossPages] = useState(false);
  const [excludedEnquiries, setExcludedEnquiries] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isSidebarFilterOpen, setIsSidebarFilterOpen] = useState(false);
  const [saveEditToken, setSaveEditToken] = useState(null);

  const { permissions } = usePermissions('PAG10002');
  const { maskObject, isHiddenField } = useFieldMasking('PAG10372#PAG10002');
  const crmOperationPerms = useMemo(() => {
    if (!permissions || !Array.isArray(permissions)) return null;
    return permissions.find(p => p.ModuleCode?.includes('PAG10002')) || {};
  }, [permissions]);

  const fetchTokenForSaveEdit = async () => {
    try {
      const payload = {
        Status: "",
        Message: "",
        Details: {
          LoginName: localStorage.getItem("LoginName") || localStorage.getItem("username") || localStorage.getItem("UserLogin") || "Abhi01",
          Password: localStorage.getItem("Password") || localStorage.getItem("password") || localStorage.getItem("UserPassword") || "770940",
          Url: localStorage.getItem("Url") || localStorage.getItem("domain") || "www.kit19.com",
          DeviceId: localStorage.getItem("DeviceId") || localStorage.getItem("deviceId") || localStorage.getItem("DEVICE_ID") || "10-60-4B-7A-92-E5"
        }
      };

      const response = await fetch(GET_TOKEN_FOR_SAVE_EDIT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Read as raw text to avoid JS precision loss on large integer tokens
        const rawText = await response.text();

        // Check Status === 1 first
        const statusMatch = rawText.match(/"Status"\s*:\s*(\d+)/);
        if (statusMatch && statusMatch[1] === '1') {
          // Extract Token as string using regex to preserve full precision
          const tokenMatch = rawText.match(/"Token"\s*:\s*(-?\d+)/);
          if (tokenMatch && tokenMatch[1]) {
            const tokenStr = tokenMatch[1]; // Keep as string — never parse to Number
            setSaveEditToken(tokenStr);
            return tokenStr;
          }
        }
      }
    } catch (error) {
      console.error("Error fetching token for save/edit:", error);
    }
    return null;
  };

  useEffect(() => {
    fetchTokenForSaveEdit();
  }, []);

  // Sync selected enquiries to localStorage as 'hasid' for mass operations
  useEffect(() => {
    localStorage.setItem('hasid', selectedEnquiries.join(','));
  }, [selectedEnquiries]);
  const [massOperation, setMassOperation] = useState('');
  const [showMassOperationPanel, setShowMassOperationPanel] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ show: false, type: 'success', title: '', message: '', onConfirm: null });
  const [showImport, setShowImport] = useState(false);
  const [activeOperationMode, setActiveOperationMode] = useState('');
  const [showViewOptions, setShowViewOptions] = useState(false);
  const [showCallWidget, setShowCallWidget] = useState(false);
  const [callWidgetData, setCallWidgetData] = useState(null);
  const [customFieldsList, setCustomFieldsList] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('enquiry_visible_columns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        delete parsed.leadNo;
        delete parsed.status;
        delete parsed.undefined;
        delete parsed.custom_undefined;
        
        Object.keys(parsed).forEach(k => {
          if (k === 'undefined' || k.includes('undefined')) {
            delete parsed[k];
          }
        });
        
        return parsed;
      } catch (e) {
        console.error("Error parsing saved columns:", e);
      }
    }
    return {
      enquiryDetails: true,
      mobileNumber: true,
      email: true,
      enquiryId: false,
      score: false,
      creationDetails: true,
      remarks: true,
      source: true,
      country: true,
      state: true,
      city: true,
      address: true,
      medium: true,
      type: true,
      campaign: true,
      isduplicateEmail: true,
      isduplicateMobile: true,
      stages: true,
      stagesId: true,
      owner: true,
      createdBy: true
    };
  });

  useEffect(() => {
    localStorage.setItem('enquiry_visible_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);
  const [activeFilter, setActiveFilter] = useState(urlFilter || 'All');
  const [viewMode, setViewMode] = useState('table');
  const [sortConfig, setSortConfig] = useState({ key: 'CreatedDate', direction: 'desc' });

  // PDF Export State
  const [showPdfPanel, setShowPdfPanel] = useState(false);

  const [statsCounts, setStatsCounts] = useState({ total: 0, open: 0, lead: 0 });
  const [isScheduleCallOpen, setIsScheduleCallOpen] = useState(false);
  const [selectedIdForCall, setSelectedIdForCall] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [customSearchOptions, setCustomSearchOptions] = useState([]);
  const [selectedCustomSearch, setSelectedCustomSearch] = useState(urlSearchName || '');
  const [appliedCustomSearchName, setAppliedCustomSearchName] = useState(urlSearchName || '');
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [isCustomSearchExpanded, setIsCustomSearchExpanded] = useState(!!urlSearchName);

  useEffect(() => {
    if (urlSearchName) {
      setAppliedCustomSearchName(urlSearchName);
      setSelectedCustomSearch(urlSearchName);
      setIsCustomSearchExpanded(true);
    }
  }, [urlSearchName]);

  const [isDashboardFiltered, setIsDashboardFiltered] = useState(!!urlFilter);
  const [dashboardFilterLabel, setDashboardFilterLabel] = useState(urlFilter ? urlFilter.replace(/([A-Z])/g, ' $1').trim() : '');

  useEffect(() => {
    if (urlFilter) {
      setActiveFilter(urlFilter);
      setIsDashboardFiltered(true);
      setDashboardFilterLabel(urlFilter.replace(/([A-Z])/g, ' $1').trim());
    }
  }, [urlFilter]);

  const enquiryFilterFields = [
    { label: 'Person Name', value: 'PersonName' },
    { label: 'Mobile No', value: 'MobileNo' },
    { label: 'Email ID', value: 'EmailID' },
    { label: 'Company Name', value: 'CompanyName' },
    { label: 'Enquiry Id', value: 'EnquiryId' },
    { label: 'Source', value: 'SourceName' },
    { label: 'Medium', value: 'MediumName' },
    { label: 'Campaign', value: 'CampaignName' },
    { label: 'Country', value: 'CountryName' },
    { label: 'State', value: 'StateName' },
    { label: 'City', value: 'CityName' },
    { label: 'Address', value: 'Address' },
    { label: 'Remarks', value: 'Remarks' },
    { label: 'Owner', value: 'User_login' },
    { label: 'Created Date', value: 'CreatedDate', type: 'date' }
  ];
  const API_BASE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
  const SERVICE_AZURE_URL_DEVE_1 = (process.env.REACT_APP_BACKEND_BASEURL_FOR_DEVELOPMENT_STAGE1 || '').replace(/\/$/, '');
  const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
  const LOCAL_BASE_URL = "http://localhost:62194/";
  const WCF_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
  const Enquiry_ShowWData = `${SERVICE_AZURE_URL_DEVE_1}${API_ENDPOINTS.ENQUIRIES.ENQUIRY_DEATILS}`;
  const TOKEN = (process.env.REACT_APP_TOKE_UNIVERSAL_TOKEN || '-2295521862261168');
  const Prefrence_Cols = `${SERVICE_AZURE_URL_DEVE_1}${API_ENDPOINTS.ENQUIRIES.GET_PREFRENCE_COL}`;
  const Get_Enquiry_Data = `${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.ENQUIRY_DETAILS_LIST}`;
  const Mass_Delete = `${WCF_BASE}${API_ENDPOINTS.ENQUIRIES.MASS_DELETE_ENQUIRY}`;
  const Get_Enquiry_Details = `${API_BASE}${API_ENDPOINTS.DASHBOARD.GET_ENQUIRY_DETAILS}`;
  const Create_Meeting_Save = `${WCF_BASE}${API_ENDPOINTS.ENQUIRIES.CREATE_MEETING_SAVE}`;
  const Custom_Search = `${API_BASE}${API_ENDPOINTS.ENQUIRIES.CUSTOM_SEARCH_DROPDOWN}`;
  const Bind_Mass_Update = `${WCF_BASE}${API_ENDPOINTS.ENQUIRIES.MASS_UPDATE_BIND_FIELD}`
  const Mass_Update_Save = `${WCF_BASE}${API_ENDPOINTS.ENQUIRIES.MASS_UPDATE_SAVE}`
  const Export_ENquiry_Default = `${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.EXPORT_ENQUIRY_DEFAULT}`
  const Get_Custom_Fields_New = `${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.GET_CUSTOM_FIELDS_NEW}`;
  const GET_TOKEN_FOR_SAVE_EDIT = `${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.GET_TOKEN}`;
  const handlePdfIconClick = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowPdfPanel(true);
  };

  // enrichment is no longer needed with the new API as it returns all fields
  const handleRefresh = () => {
    setSearchText('');
    setAdvancedFilters(null);
    setActiveFilter('All');
    setSelectedCustomSearch('');
    setAppliedCustomSearchName('');
    setIsCustomSearchExpanded(false);
    setIsSearchExpanded(false);
    setStartIndex(0);
    setSelectedEnquiries([]);
    setExcludedEnquiries([]);
    setIsAllSelectedAcrossPages(false);
    setSortConfig({ key: 'CreatedDate', direction: 'desc' });

    // Immediate fetch
    fecthEnquiry(0, false);
    fetchStats();
  };

  const fetchSingleEnquiryDetails = async (enquiryId) => {
    try {
      const payload = {
        Token: TokenId,
        Details: {
          txt_search: "",
          SearchText: "",
          TextSearch: "",
          FilterText: `EnquiryId=${enquiryId}`,
          parent_id: Number(parentId),
          ParentId: Number(parentId),
          UserId: Number(userId),
          enno: "20",
          filter_text: `EnquiryId=${enquiryId}`,
          order_str: "CreatedDate desc",
          srno: "1",
          ue_query: "",
          user_id: Number(userId),
          custom_search_id: 0,
          CustomSearchId: 0
        }
      };

      const response = await fetch(Get_Enquiry_Data, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        return result.Details?.[0] || null;
      }
    } catch (error) {
      console.error("Error fetching single enquiry details:", error);
    }
    return null;
  };



  const fecthEnquiry = async (startValue = 0, loadMore = false, overrideFilters = null) => {
    setIsLoading(true);
    // Sync starting index if provided (e.g. from handlePageChange)
    if (!loadMore) setStartIndex(startValue);

    const limit = parseInt(itemsPerPage) || 20;
    const startNo = startValue + 1;
    const endNo = startValue + limit;

    // Sort logic
    const orderStr = sortConfig.key ? `${sortConfig.key} ${sortConfig.direction}` : "";

    // Filter logic
    let conditions = [];

    // Base filter (Open/Lead) - Updated to match new backend requirements
    if (activeFilter === 'Open' || activeFilter === 'StillOpen') {
      conditions.push("(Isopen = 'open')");
    } else if (activeFilter === 'Lead') {
      conditions.push("(Isopen = 'lead')");
    } else if (activeFilter === 'Today') {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      const todayStr = `${y}-${m}-${d}`;
      conditions.push(`(CreatedDate >= '${todayStr} 00:00:00' AND CreatedDate <= '${todayStr} 23:59:59')`);
    } else if (activeFilter === 'ThisMonth') {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth();
      const firstDay = new Date(y, m, 1);
      const lastDay = new Date(y, m + 1, 0);

      const formatYMD = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const startStr = formatYMD(firstDay);
      const endStr = formatYMD(lastDay);
      conditions.push(`(CreatedDate >= '${startStr} 00:00:00' AND CreatedDate <= '${endStr} 23:59:59')`);
    }

    const currentFilters = overrideFilters !== null ? overrideFilters : advancedFilters;
    if (currentFilters && currentFilters.rows && currentFilters.rows.length > 0) {
      const { logic, rows } = currentFilters;
      const rowStrings = rows.map(row => {
        const val = String(row.value || '').replace(/'/g, "''"); // Escape single quotes
        const op = row.operator === 'contains' || row.operator === 'starts_with' || row.operator === 'ends_with' ? 'LIKE' :
          row.operator === 'not_contains' ? 'NOT LIKE' :
            row.operator === 'not_equal' ? '<>' : '=';

        let finalVal = val;
        if (row.operator === 'contains' || row.operator === 'not_contains') finalVal = `%${val}%`;
        else if (row.operator === 'starts_with') finalVal = `${val}%`;
        else if (row.operator === 'ends_with') finalVal = `%${val}`;

        return `${row.field} ${op} '${finalVal}'`;
      });
      if (rowStrings.length > 0) {
        conditions.push(`(${rowStrings.join(logic === 'ALL' ? ' AND ' : ' OR ')})`);
      }
    }

    if (reportAction) {
      const srcStr = sourceName || "";
      const medStr = mediumName || "";
      const cmpStr = campaignName || "";

      if (reportAction === 'NewEnquiries') {
        conditions.push(`(CAST(CreatedDate AS DATE) = '${reportDate}') AND ISNULL(SourceName,'') = '${srcStr}' AND ISNULL(MediumName,'') = '${medStr}' AND ISNULL(CampaignName,'') = '${cmpStr}'`);
      } 
      else if (reportAction === 'EnquiriesConvertedToLeads') {
        conditions.push(`(CAST(CreatedDate AS DATE) = '${reportDate}') AND ISNULL(SourceName,'') = '${srcStr}' AND ISNULL(MediumName,'') = '${medStr}' AND ISNULL(CampaignName,'') = '${cmpStr}' AND Isopen = 'lead'`);
      }
      else if (reportAction === 'EnquiriesmatchingLeads') {
        conditions.push(`(CAST(CreatedDate AS DATE) = '${reportDate}') AND ISNULL(SourceName,'') = '${srcStr}' AND ISNULL(MediumName,'') = '${medStr}' AND ISNULL(CampaignName,'') = '${cmpStr}' AND Isopen = 'lead'`);
      }
      else if (reportAction === 'OpenEnquiries') {
        conditions.push(`(CAST(CreatedDate AS DATE) = '${reportDate}') AND ISNULL(SourceName,'') = '${srcStr}' AND ISNULL(MediumName,'') = '${medStr}' AND ISNULL(CampaignName,'') = '${cmpStr}' AND Isopen = 'open'`);
      }
      else if (reportAction === 'EnquiriesNotmatchingLeads') {
        conditions.push(`(CAST(CreatedDate AS DATE) = '${reportDate}') AND ISNULL(SourceName,'') = '${srcStr}' AND ISNULL(MediumName,'') = '${medStr}' AND ISNULL(CampaignName,'') = '${cmpStr}' AND Isopen = 'open'`);
      }
      else if (reportAction === 'AgentwiseNewEnquiries') {
         conditions.push(`(CAST(CreatedDate AS DATE) = '${reportDate}') AND CreatedBy = ${createdBy}`);
      }
      else if (reportAction === 'AgentwiseEnquiriesConvertedToLeads') {
         conditions.push(`(CAST(CreatedDate AS DATE) = '${reportDate}') AND CreatedBy = ${createdBy} AND Isopen = 'lead'`);
      }
      else if (reportAction === 'AgentwiseEnquiriesmatchingLeads') {
         conditions.push(`(CAST(CreatedDate AS DATE) = '${reportDate}') AND CreatedBy = ${createdBy} AND Isopen = 'lead'`);
      }
      else if (reportAction === 'AgentwiseOpenEnquiries') {
         conditions.push(`(CAST(CreatedDate AS DATE) = '${reportDate}') AND CreatedBy = ${createdBy} AND Isopen = 'open'`);
      }
      else if (reportAction === 'AgentwiseEnquiriesNotmatchingLeads') {
         conditions.push(`(CAST(CreatedDate AS DATE) = '${reportDate}') AND CreatedBy = ${createdBy} AND Isopen = 'open'`);
      }
      else if (reportAction === 'TotalNewEnquiries') {
         conditions.push(`(CAST(CreatedDate AS DATE) >= '${reportFromDate}') AND (CAST(CreatedDate AS DATE) <= '${reportToDate}')`);
      }
      else if (reportAction === 'TotalEnquiriesConvertedToLeads') {
         conditions.push(`(CAST(CreatedDate AS DATE) >= '${reportFromDate}') AND (CAST(CreatedDate AS DATE) <= '${reportToDate}') AND Isopen = 'lead'`);
      }
      else if (reportAction === 'TotalEnquiriesmatchingLeads') {
         conditions.push(`(CAST(CreatedDate AS DATE) >= '${reportFromDate}') AND (CAST(CreatedDate AS DATE) <= '${reportToDate}') AND Isopen = 'lead'`);
      }
      else if (reportAction === 'TotalOpenEnquiries') {
         conditions.push(`(CAST(CreatedDate AS DATE) >= '${reportFromDate}') AND (CAST(CreatedDate AS DATE) <= '${reportToDate}') AND Isopen = 'open'`);
      }
      else if (reportAction === 'TotalEnquiriesNotmatchingLeads') {
         conditions.push(`(CAST(CreatedDate AS DATE) >= '${reportFromDate}') AND (CAST(CreatedDate AS DATE) <= '${reportToDate}') AND Isopen = 'open'`);
      }
      else if (reportAction === 'TotalAgentwiseNewEnquiries') {
         conditions.push(`(CAST(CreatedDate AS DATE) >= '${reportFromDate}') AND (CAST(CreatedDate AS DATE) <= '${reportToDate}')`);
      }
      else if (reportAction === 'TotalAgentwiseEnquiriesConvertedToLeads') {
         conditions.push(`(CAST(CreatedDate AS DATE) >= '${reportFromDate}') AND (CAST(CreatedDate AS DATE) <= '${reportToDate}') AND Isopen = 'lead'`);
      }
      else if (reportAction === 'TotalAgentwiseEnquiriesmatchingLeads') {
         conditions.push(`(CAST(CreatedDate AS DATE) >= '${reportFromDate}') AND (CAST(CreatedDate AS DATE) <= '${reportToDate}') AND Isopen = 'lead'`);
      }
      else if (reportAction === 'TotalAgentwiseOpenEnquiries') {
         conditions.push(`(CAST(CreatedDate AS DATE) >= '${reportFromDate}') AND (CAST(CreatedDate AS DATE) <= '${reportToDate}') AND Isopen = 'open'`);
      }
      else if (reportAction === 'TotalAgentwiseEnquiriesNotmatchingLeads') {
         conditions.push(`(CAST(CreatedDate AS DATE) >= '${reportFromDate}') AND (CAST(CreatedDate AS DATE) <= '${reportToDate}') AND Isopen = 'open'`);
      }
    }

    let finalFilterText = conditions.join(' AND ');
    if (searchText) {
      const s = searchText.trim();
      let searchSql = "";
      if (/^\d+$/.test(s)) {
        // Only integers -> Mobile number search
        searchSql = `((Personname like 'INPUT%') or (Mobileno like '%${s}%') or (Emailid like 'Input%'))`;
      } else if (s.includes('@')) {
        // Text with @ -> Email search
        searchSql = `((Personname like 'INPUT%') or (Mobileno like '%INPUT%') or (Emailid like '${s}%'))`;
      } else {
        // Other text -> Name search
        searchSql = `((Personname like '${s}%') or (Mobileno like '%INPUT%') or (Emailid like 'Input%'))`;
      }
      finalFilterText = finalFilterText ? `(${finalFilterText}) AND ${searchSql}` : searchSql;
    }

    // Predefined Search Logic (Functional Parity with WebForm)
    let ueQuery = "";
    let finalSearchName = appliedCustomSearchName || "";

    if (finalSearchName.toLowerCase().includes("(predefined)")) {
      const lowerName = finalSearchName.toLowerCase().trim();
      if (lowerName === "enquiriescreatedbyme(predefined)") {
        ueQuery = `( CreatedBy = ${userId} )`;
      } else if (lowerName === "recentlyadded(predefined)") {
        ueQuery = " ( DATEDIFF(DAY,CreatedDate,getdate()) between 0 and 7 ) ";
      } else if (lowerName === "recentlyaddedbyme(predefined)") {
        ueQuery = `(( DATEDIFF(DAY,CreatedDate,getdate()) between 0 and 7 ) and ( CreatedBy = ${userId} ))`;
      } else if (lowerName === "openenquiries(predefined)") {
        ueQuery = " ( isopen = 'open' ) ";
      } else if (lowerName === "addedmergedtoleadtoday(predefined)") {
        ueQuery = " ( ( cast(CreatedDate as date) = cast(getdate() as date)) and ( isopen = 'lead' ) ) ";
      } else if (lowerName === "addedmergedtolead(predefined)") {
        ueQuery = " ( isopen = 'lead' ) ";
      }

      // If it's a predefined search, the SP expects SearchName to be empty when ue_query is provided
      if (ueQuery) {
        finalSearchName = "";
      }
    }

    // Helper to find the selected custom search object and extract its filter text and ID
    let customSearchFilterText = "";
    let customSearchIdNum = 0;
    if (appliedCustomSearchName && !appliedCustomSearchName.toLowerCase().includes("(predefined)")) {
      const foundOpt = customSearchOptions.find(opt => {
        const val = opt.CriteriaName || opt.Text || opt.Name || opt.SearchName || (typeof opt === 'string' ? opt : '');
        return val === appliedCustomSearchName;
      });
      if (foundOpt && typeof foundOpt === 'object') {
        customSearchFilterText = foundOpt.FilterText || foundOpt.FilterCondition || foundOpt.SearchText || foundOpt.Query || foundOpt.Conditions || foundOpt.filterText || foundOpt.filterCondition || "";
        customSearchIdNum = foundOpt.ID || foundOpt.Id || foundOpt.SearchCriteria_ID || foundOpt.SearchId || foundOpt.searchId || foundOpt.id || 0;
      }
    }

    // Combine customSearchFilterText with finalFilterText
    let combinedFilterText = finalFilterText;
    if (customSearchFilterText) {
      combinedFilterText = combinedFilterText ? `(${combinedFilterText}) AND (${customSearchFilterText})` : customSearchFilterText;
    }

    const isCustomSearchActive = appliedCustomSearchName && !appliedCustomSearchName.toLowerCase().includes("(predefined)");
    const hasAdditionalFilters = !!(searchText || (advancedFilters && advancedFilters.rows && advancedFilters.rows.length > 0) || activeFilter !== 'All');

    const useNewCustomSearchApi = isCustomSearchActive && !hasAdditionalFilters;
    const endpointUrl = useNewCustomSearchApi ? `${NEWV3_BASE_URL}/Enquiry/GetEnquiryListByCustomSearch` : Get_Enquiry_Data;

    const payload = useNewCustomSearchApi ? {
      Token: TokenId,
      Details: {
        search_name: appliedCustomSearchName,
        user_id: Number(userId),
        srno: startNo.toString(),
        enno: endNo.toString()
      }
    } : {
      Token: TokenId,
      Details: {
        txt_search: "",
        SearchText: searchText || "",
        TextSearch: searchText || "",
        FilterText: combinedFilterText || "",
        SearchName: finalSearchName,
        custom_search_id: Number(customSearchIdNum || urlCustomSearchId || 0),
        CustomSearchId: Number(customSearchIdNum || urlCustomSearchId || 0),
        ParentId: Number(parentId),
        UserId: Number(userId),
        parent_id: Number(parentId),
        user_id: Number(userId),
        filter_text: combinedFilterText || "",
        order_str: orderStr,
        srno: startNo.toString(),
        enno: endNo.toString(),
        ue_query: ueQuery
      }
    };


    try {
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Network response was not ok');
      const result = await response.json();

      const rawData = result.Details || [];
      const total = rawData.length > 0 ? (rawData[0].rcnt || rawData[0].rc1 || rawData[0].TotalRow || rawData[0].TotalRecords || rawData.length) : 0;
      setTotalRecord(total);

      // Map fields to match existing UI logic
      const mappedData = rawData.map(enq => ({
        ...enq,
        Source: enq.SourceName || '-',
        Medium: enq.MediumName || '-',
        CsvMobileNo: enq.MobileNo || enq.MobileNo3 || '-',
        CsvEmailId: enq.EmailID || enq.EmailID3 || '-',
        IsOpen: enq.Isopen === 'open' || enq.Isopen === true || enq.Isopen === 'True',
        Stage: enq.Stages || 'RAW',
        OwnerName: enq.User_login || enq.CreatedBy || '-',
        OwnerId: (enq.User_login && enq.User_login.match(/\d+/)) ? enq.User_login.match(/\d+/)[0] : ((enq.CreatedBy && enq.CreatedBy.match(/\d+/)) ? enq.CreatedBy.match(/\d+/)[0] : (enq.UserId || enq.UserID || enq.user_id || '-')),
      }));

      if (loadMore) {
        setEnquiries(prev => [...prev, ...mappedData]);
      } else {
        setEnquiries(mappedData);
        if (mappedData.length > 0) {
          setSelectedLead(mappedData[0].EnquiryId);
          setSelectedEnquiry(mappedData[0]);
        } else {
          setSelectedLead(null);
          setSelectedEnquiry(null);
        }
      }


    } catch (error) {
      console.error('Error fetching enquiries:', error);
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  };

  const fetchStats = async () => {
    const getCount = async (filter) => {
      // Helper to find the selected custom search object and extract its filter text and ID
      let customSearchFilterText = "";
      let customSearchIdNum = 0;
      if (appliedCustomSearchName && !appliedCustomSearchName.toLowerCase().includes("(predefined)")) {
        const foundOpt = customSearchOptions.find(opt => {
          const val = opt.CriteriaName || opt.Text || opt.Name || opt.SearchName || (typeof opt === 'string' ? opt : '');
          return val === appliedCustomSearchName;
        });
        if (foundOpt && typeof foundOpt === 'object') {
          customSearchFilterText = foundOpt.FilterText || foundOpt.FilterCondition || foundOpt.SearchText || foundOpt.Query || foundOpt.Conditions || foundOpt.filterText || foundOpt.filterCondition || "";
          customSearchIdNum = foundOpt.ID || foundOpt.Id || foundOpt.SearchCriteria_ID || foundOpt.SearchId || foundOpt.searchId || foundOpt.id || 0;
        }
      }

      let combinedFilterText = filter;
      if (customSearchFilterText) {
        combinedFilterText = combinedFilterText ? `(${combinedFilterText}) AND (${customSearchFilterText})` : customSearchFilterText;
      }

      const payload = {
        Token: TokenId,
        Details: {
          txt_search: "",
          SearchText: "",
          TextSearch: "",
          FilterText: combinedFilterText || "",
          SearchName: (appliedCustomSearchName && !appliedCustomSearchName.toLowerCase().includes("(predefined)")) ? appliedCustomSearchName : "",
          custom_search_id: Number(customSearchIdNum || urlCustomSearchId || 0),
          CustomSearchId: Number(customSearchIdNum || urlCustomSearchId || 0),
          parent_id: parentId,
          ParentId: parentId,
          user_id: userId,
          UserId: userId,
          filter_text: combinedFilterText || "",
          order_str: "CreatedDate desc",
          srno: "1",
          enno: "20",
          ue_query: ""
        }
      };
      try {
        const response = await fetch(Get_Enquiry_Data, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          const result = await response.json();
          const rawData = result.Details || [];
          return rawData.length > 0 ? (rawData[0].rcnt || rawData[0].rc1 || 0) : 0;
        }
      } catch (err) {
        console.error(`Error fetching count for ${filter}:`, err);
      }
      return 0;
    };

    const getMatrixStats = async () => {
      const payload = {
        Details: {
          UserId: userId?.toString() || "",
          strEnquirySection: "enquiries"
        },
        Message: "",
        Status: "",
        Token: TokenId
      };
      try {
        const response = await fetch(Get_Enquiry_Details, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          const result = await response.json();
          const matrix = result.Details?.EnquiryMatrix_CLS_List || [];
          if (matrix.length > 0) {
            return parseInt(matrix[0].StillOpen) || 0;
          }
        }
      } catch (err) {
        console.error("Error fetching matrix stats:", err);
      }
      return 0;
    };

    const [totalCount, openCount, leadCount] = await Promise.all([
      getCount(""),
      getCount("(Isopen = 'open')"),
      getCount("(Isopen = 'lead')")
    ]);

    setStatsCounts({
      total: totalCount,
      open: openCount,
      lead: leadCount
    });
  };
  ;


  const handleLoadMore = () => {
    const newStart = startIndex + itemsPerPage;
    setStartIndex(newStart);
    fecthEnquiry(newStart, true);
  };

  const handleExportClick = async () => {
    setIsLoading(true);
    try {
      // Reconstruct the filter text just like fetchEnquiry
      let conditions = [];
      if (activeFilter === 'Open') conditions.push("(Isopen = 'open')");
      else if (activeFilter === 'Lead') conditions.push("(Isopen = 'lead')");

      if (advancedFilters && advancedFilters.rows && advancedFilters.rows.length > 0) {
        const { logic, rows } = advancedFilters;
        const rowStrings = rows.map(row => {
          const val = String(row.value || '').replace(/'/g, "''"); // Escape single quotes
          const op = row.operator === 'contains' || row.operator === 'starts_with' || row.operator === 'ends_with' ? 'LIKE' :
            row.operator === 'not_contains' ? 'NOT LIKE' :
              row.operator === 'not_equal' ? '<>' : '=';

          let finalVal = val;
          if (row.operator === 'contains' || row.operator === 'not_contains') finalVal = `%${val}%`;
          else if (row.operator === 'starts_with') finalVal = `${val}%`;
          else if (row.operator === 'ends_with') finalVal = `%${val}`;

          return `${row.field} ${op} '${finalVal}'`;
        });
        if (rowStrings.length > 0) {
          conditions.push(`(${rowStrings.join(logic === 'ALL' ? ' AND ' : ' OR ')})`);
        }
      }

      let finalFilterText = conditions.join(' AND ');
      if (searchText) {
        const s = searchText.trim();
        let searchSql = "";
        if (/^\d+$/.test(s)) {
          searchSql = `((Personname like 'INPUT%') or (Mobileno like '%${s}%') or (Emailid like 'Input%'))`;
        } else if (s.includes('@')) {
          searchSql = `((Personname like 'INPUT%') or (Mobileno like '%INPUT%') or (Emailid like '${s}%'))`;
        } else {
          searchSql = `((Personname like '${s}%') or (Mobileno like '%INPUT%') or (Emailid like 'Input%'))`;
        }
        finalFilterText = finalFilterText ? `(${finalFilterText}) AND ${searchSql}` : searchSql;
      }

      // Let the backend SP use its default sorting
      let orderStr = "";

      // Predefined Search Logic for Export
      let ueQuery = "";
      if (appliedCustomSearchName && appliedCustomSearchName.toLowerCase().includes("(predefined)")) {
        const lowerName = appliedCustomSearchName.toLowerCase().trim();
        if (lowerName === "enquiriescreatedbyme(predefined)") {
          ueQuery = `( CreatedBy = ${userId} )`;
        } else if (lowerName === "recentlyadded(predefined)") {
          ueQuery = " ( DATEDIFF(DAY,CreatedDate,getdate()) between 0 and 7 ) ";
        } else if (lowerName === "recentlyaddedbyme(predefined)") {
          ueQuery = `(( DATEDIFF(DAY,CreatedDate,getdate()) between 0 and 7 ) and ( CreatedBy = ${userId} ))`;
        } else if (lowerName === "openenquiries(predefined)") {
          ueQuery = " ( isopen = 'open' ) ";
        } else if (lowerName === "addedmergedtoleadtoday(predefined)") {
          ueQuery = " ( ( cast(CreatedDate as date) = cast(getdate() as date)) and ( isopen = 'lead' ) ) ";
        } else if (lowerName === "addedmergedtolead(predefined)") {
          ueQuery = " ( isopen = 'lead' ) ";
        }
      }

      // Helper to find the selected custom search object and extract its filter text and ID
      let customSearchFilterText = "";
      let customSearchIdNum = 0;
      if (appliedCustomSearchName && !appliedCustomSearchName.toLowerCase().includes("(predefined)")) {
        const foundOpt = customSearchOptions.find(opt => {
          const val = opt.CriteriaName || opt.Text || opt.Name || opt.SearchName || (typeof opt === 'string' ? opt : '');
          return val === appliedCustomSearchName;
        });
        if (foundOpt && typeof foundOpt === 'object') {
          customSearchFilterText = foundOpt.FilterText || foundOpt.FilterCondition || foundOpt.SearchText || foundOpt.Query || foundOpt.Conditions || foundOpt.filterText || foundOpt.filterCondition || "";
          customSearchIdNum = foundOpt.ID || foundOpt.Id || foundOpt.SearchCriteria_ID || foundOpt.SearchId || foundOpt.searchId || foundOpt.id || 0;
        }
      }

      // Combine customSearchFilterText with finalFilterText
      let combinedFilterText = finalFilterText;
      if (customSearchFilterText) {
        combinedFilterText = combinedFilterText ? `(${combinedFilterText}) AND (${customSearchFilterText})` : customSearchFilterText;
      }

      // ── DECISION: client-side vs API export ─────────────────────────────────
      // When search text OR advanced-filter rows are active → export from the
      // already-loaded enquiries state (what the user currently sees on screen).
      // For tab filters (Open/Lead), custom search, or no filter → use API.
      const hasSearchText = !!(searchText && searchText.trim());
      const hasAdvancedFilter = !!(advancedFilters && advancedFilters.rows && advancedFilters.rows.length > 0);
      const hasCustomSearch = !!(appliedCustomSearchName && appliedCustomSearchName.trim() && !appliedCustomSearchName.toLowerCase().includes("(predefined)"));

      if (hasSearchText || hasAdvancedFilter || hasCustomSearch) {
        const useNewCustomSearchApi = hasCustomSearch && !hasSearchText && !hasAdvancedFilter;
        const exportEndpointUrl = useNewCustomSearchApi ? `${NEWV3_BASE_URL}/Enquiry/GetEnquiryListByCustomSearch` : Get_Enquiry_Data;

        const fetchAllPayload = useNewCustomSearchApi ? {
          Token: TokenId,
          Details: {
            search_name: appliedCustomSearchName,
            user_id: Number(userId),
            srno: "1",
            enno: totalRecord > 0 ? totalRecord.toString() : "9999"
          }
        } : {
          Token: TokenId,
          Details: {
            txt_search: "",
            SearchText: searchText || "",
            TextSearch: searchText || "",
            FilterText: combinedFilterText || "",
            SearchName: (appliedCustomSearchName && !appliedCustomSearchName.toLowerCase().includes("(predefined)")) ? appliedCustomSearchName : "",
            custom_search_id: Number(customSearchIdNum || urlCustomSearchId || 0),
            CustomSearchId: Number(customSearchIdNum || urlCustomSearchId || 0),
            ParentId: Number(parentId),
            UserId: Number(userId),
            parent_id: Number(parentId),
            user_id: Number(userId),
            filter_text: combinedFilterText || "",
            order_str: sortConfig.key ? `${sortConfig.key} ${sortConfig.direction}` : "CreatedDate desc",
            srno: "1",
            enno: totalRecord > 0 ? totalRecord.toString() : "9999",
            ue_query: ueQuery
          }
        };

        const allResponse = await fetch(exportEndpointUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fetchAllPayload)
        });

        if (!allResponse.ok) throw new Error('Failed to fetch all records for export');
        const allResult = await allResponse.json();
        const allRaw = allResult.Details || [];

        if (allRaw.length === 0) {
          setAlertConfig({
            show: true,
            type: 'error',
            title: 'Export Failed',
            message: 'No records found to export.',
            onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
          });
          return;
        }

        // Export every field returned by the API (same as API export format)
        const exportData = allRaw.map(enq => maskObject({ ...enq }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        
        // Auto-size columns based on content
        if (exportData.length > 0) {
          const keys = Object.keys(exportData[0]);
          worksheet['!cols'] = keys.map(key => {
            let maxLen = String(key).length;
            exportData.forEach(row => {
              const val = row[key];
              if (val !== null && val !== undefined) {
                const len = String(val).length;
                if (len > maxLen) maxLen = len;
              }
            });
            return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
          });
        }
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Enquiries');
        const fileName = `EnquiryExport_${new Date().toISOString().replace(/[:.-]/g, '')}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        setAlertConfig({
          show: true,
          type: 'success',
          title: 'Export Successful',
          message: `${exportData.length} record(s) exported successfully.`,
          onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
        });
        return; // done — skip API path
      }

      // ── API EXPORT (unchanged) ───────────────────────────────────────────────
      const payload = {
        Token: TokenId,
        Details: {
          UserId: Number(userId),
          ParentId: Number(parentId),
          FilterText: combinedFilterText || "",
          SearchText: searchText || "",
          OrderBy: orderStr, // Pass empty to prevent SQL syntax errors
          TotalRecords: totalRecord.toString(),
          IsAdmin: true,
          UeQuery: ueQuery,
          CustomSearchId: Number(customSearchIdNum || urlCustomSearchId || 0),
          SearchName: (appliedCustomSearchName && !appliedCustomSearchName.toLowerCase().includes("(predefined)")) ? appliedCustomSearchName : ""
        }
      };

      const response = await fetch(Export_ENquiry_Default, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Network response was not ok');
      const result = await response.json();

      if (result.Status === 1 && result.Details && result.Details.data && result.Details.data.length > 0) {
        const data = result.Details.data.map(enq => maskObject({ ...enq }));
        // Convert to Excel
        const worksheet = XLSX.utils.json_to_sheet(data);

        // Auto-size columns based on content
        if (data.length > 0) {
          const keys = Object.keys(data[0]);
          worksheet['!cols'] = keys.map(key => {
            let maxLen = String(key).length;
            data.forEach(row => {
              const val = row[key];
              if (val !== null && val !== undefined) {
                const len = String(val).length;
                if (len > maxLen) maxLen = len;
              }
            });
            return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
          });
        }
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");
        const fileName = `EnquiryExport_${new Date().toISOString().replace(/[:.-]/g, '')}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        setAlertConfig({
          show: true,
          type: 'success',
          title: 'Export Successful',
          message: `${data.length} records exported successfully.`,
          onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
        });
      } else {
        // If data is empty but totalRecord > 0, we can assume it was queued if that's what the backend does
        if (totalRecord > 0) {
          setAlertConfig({
            show: true,
            type: 'info',
            title: 'Export Queued',
            message: 'Request has been logged for background processing due to large data size.',
            onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
          });
        } else {
          setAlertConfig({
            show: true,
            type: 'error',
            title: 'Export Failed',
            message: 'No records found to export.',
            onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
          });
        }
      }

    } catch (error) {
      console.error('Error exporting enquiries:', error);
      setAlertConfig({
        show: true,
        type: 'error',
        title: 'Export Failed',
        message: 'An error occurred during export.',
        onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
      });
    } finally {
      setIsLoading(false);
    }
  };

  // fecthEnquiry effect is now handled below with activeFilter and advancedFilters

  const searchTText = () => {
    setStartIndex(0);
    fecthEnquiry(0, false);
  };

  // Check if there are more items to load
  const hasMore = enquiries.length < totalRecord;

  const statsData = [
    {
      title: statsCounts.total || 0,
      subtitle: 'Total Enquiries',
      icon: 'ri:list-unordered',
      color: 'primary',
      key: 'All'
    },
    {
      title: statsCounts.open || 0,
      subtitle: 'Open Enquiries',
      icon: 'ri:folder-open-line',
      color: 'success',
      key: 'Open'
    },
    {
      title: statsCounts.lead || 0,
      subtitle: 'Lead Enquiries',
      icon: 'ri:user-follow-line',
      color: 'info',
      key: 'Lead'
    }
  ];

  const fetchCustomSearchOptions = async () => {
    const uid = userId || localStorage.getItem('UserId') || '34594';
    setIsOptionsLoading(true);
    try {
      const baseUrl = (process.env.REACT_APP_SERVICES_API_BASE_URL || process.env.REACT_APP_SERVICES_AZURE_BASEURL || 'https://serviceskit19.azurewebsites.net').replace(/\/$/, '');
      const url = `${baseUrl}/Enquiry/GetEnquirySearchName?UserID=${uid}`;

      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        const data = result?.Details?.DT_EnquirySearchNameList || result?.Details?.DT_LeadSearchNameList || result?.Details || result?.d || result || [];
        setCustomSearchOptions(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching custom search options:", error);
    } finally {
      setIsOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCustomFieldsList();
  }, []);

  const fetchCustomFieldsList = async () => {
    try {
      const resolvedParentId = parentId || localStorage.getItem('ParentId') || "34594";
      const payload = {
        Token: "-2295521862261168",
        Details: {
          ParentId: resolvedParentId,
          Status: "Active"
        }
      };

      const Get_Custom_Fields = `${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.GET_CUSTOM_FIELDS}`;
      const response = await fetch(Get_Custom_Fields, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.Status === 1 && result.Details) {
          const fields = result.Details
            .filter(f => f.fieldname && f.field_ID && String(f.field_ID) !== 'undefined')
            .map(f => ({
              label: f.fieldname,
              key: `custom_${f.field_ID}`,
              sequence: f.sequence
            })).sort((a, b) => a.sequence - b.sequence);
          setCustomFieldsList(fields);
          
          setVisibleColumns(prev => {
            const newCols = { ...prev };
            let changed = false;
            fields.forEach(f => {
              if (newCols[f.key] === undefined) {
                newCols[f.key] = false;
                changed = true;
              }
            });
            return changed ? newCols : prev;
          });
        }
      }
    } catch (error) {
      console.error("Error fetching custom fields:", error);
    }
  };

  useEffect(() => {
    if (userId && customSearchOptions.length === 0) {
      fetchCustomSearchOptions();
    }
  }, [userId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setStartIndex(0);
      fecthEnquiry(0, false);
      fetchStats();
    }, 500);
    return () => clearTimeout(timer);
  }, [activeFilter, advancedFilters, searchText, sortConfig, appliedCustomSearchName]);

  const filteredEnquiries = useMemo(() => {
    if (!sortConfig.key) return enquiries;
    return [...enquiries].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Safe fallback for differently named properties on frontend objects
      if (sortConfig.key === 'MobileNo' && aVal === undefined) {
        aVal = a.CsvMobileNo;
        bVal = b.CsvMobileNo;
      }
      if (sortConfig.key === 'EmailID' && aVal === undefined) {
        aVal = a.CsvEmailId;
        bVal = b.CsvEmailId;
      }

      // Handle null/undefined/empty values
      if (aVal === undefined || aVal === null || aVal === '-') aVal = '';
      if (bVal === undefined || bVal === null || bVal === '-') bVal = '';

      // Chronological/Mathematical Date Sorting
      if (sortConfig.key === 'CreatedDate') {
        const aDate = aVal ? new Date(aVal) : new Date(0);
        const bDate = bVal ? new Date(bVal) : new Date(0);
        return sortConfig.direction === 'desc' ? bDate - aDate : aDate - bDate;
      }

      // Numeric Sorting
      if (sortConfig.key === 'EnquiryId' || sortConfig.key === 'LeadNo' || sortConfig.key === 'CurrentScore') {
        const aNum = Number(aVal) || 0;
        const bNum = Number(bVal) || 0;
        return sortConfig.direction === 'desc' ? bNum - aNum : aNum - bNum;
      }

      // Default String/Locale comparison
      const aStr = String(aVal);
      const bStr = String(bVal);
      return sortConfig.direction === 'desc'
        ? bStr.localeCompare(aStr, undefined, { numeric: true, sensitivity: 'base' })
        : aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [enquiries, sortConfig]);

  const mappedEvents = useMemo(() => {
    return filteredEnquiries.map(enq => ({
      id: enq.EnquiryId,
      title: enq.PersonName,
      date: new Date(enq.CreatedDate),
      status: (String(enq.IsOpen).toLowerCase() === 'true' || enq.IsOpen === true) ? 'open' : 'lead',
      original: enq
    }));
  }, [filteredEnquiries]);

  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const handleColumnToggle = (columnKey) => {
    if (columnKey === 'enquiryDetails' && visibleColumns[columnKey]) return;
    setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  const getAvatarStyle = (name) => {
    const palettes = [
      { bg: '#eff6ff', text: '#3b82f6', border: '#dbeafe' }, // Blue
      { bg: '#ecfdf5', text: '#10b981', border: '#d1fae5' }, // Green
      { bg: '#fff1f2', text: '#f43f5e', border: '#ffe4e6' }, // Rose
      { bg: '#fffbeb', text: '#f59e0b', border: '#fef3c7' }, // Amber
      { bg: '#f5f3ff', text: '#8b5cf6', border: '#ede9fe' }, // Violet
      { bg: '#ecfeff', text: '#06b6d4', border: '#cffafe' }, // Cyan
      { bg: '#f0fdf4', text: '#16a34a', border: '#dcfce7' }, // Green 2
      { bg: '#faf5ff', text: '#9333ea', border: '#f3e8ff' }, // Purple
      { bg: '#fff7ed', text: '#ea580c', border: '#ffedd5' }, // Orange
      { bg: '#fdf2f8', text: '#db2777', border: '#fce7f3' }  // Pink
    ];
    if (!name || name === '-') return palettes[0];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % palettes.length;
    return palettes[index];
  };

  const getInitials = (name) => {
    if (!name || name === '-') return 'U';
    let trimmed = String(name).trim();

    // Remove leading dots or special characters (except + for phone numbers)
    if (!trimmed.startsWith('+')) {
      trimmed = trimmed.replace(/^[^a-zA-Z0-9]+/, '');
    }

    if (!trimmed) return 'U';

    if (trimmed.startsWith('+')) {
      // Show first two digits after the +
      return trimmed.replace('+', '').substring(0, 2);
    }

    // If it starts with a number, just return the first two digits
    if (/^\d/.test(trimmed)) {
      return trimmed.substring(0, 2);
    }

    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const renderEnquiryDetailContent = (item) => {
    const enq = item._original || item;
    return (
      <div className="flex-1 overflow-y-auto bg-white shadow-inner scrollbar-thin">
        <div className="px-6 py-3 border-b border-gray-100 bg-[#f8fafc] sticky top-0 z-10">
          <h2 className="text-sm font-bold text-slate-800">Enquiry Details</h2>
        </div>
        <EnquiryDetails enquiry={enq._original || enq} maskObject={maskObject} />
      </div>
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-GB', { month: 'short' });
    const year = d.getFullYear();
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    return `${day}-${month}-${year} ${time}`;
  };

  const handleInitiateCall = async (enquiry, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();

    // Fetch full details to get all emails and mobile numbers for widget actions
    const fullDetails = await fetchSingleEnquiryDetails(enquiry.EnquiryId);

    setCallWidgetData({
      toMobileNo: fullDetails?.MobileNo || enquiry?.__original?.CsvMobileNo || enquiry.CsvMobileNo || enquiry?.__original?.MobileNo || enquiry.MobileNo,
      entityId: enquiry.EnquiryId,
      entityName: 'enquiry',
      personName: enquiry?.__original?.PersonName || enquiry.PersonName,
      triggerRect: rect,
      toEmail: fullDetails?.EmailID || enquiry?.__original?.EmailID || enquiry.EmailID,
      extraContactInfo: fullDetails ? {
        emails: [fullDetails.EmailID, fullDetails.EmailID1, fullDetails.EmailID2, fullDetails.EmailID3].filter(Boolean),
        mobiles: [fullDetails.MobileNo, fullDetails.MobileNo1, fullDetails.MobileNo2, fullDetails.MobileNo3].filter(Boolean)
      } : null
    });
    setShowCallWidget(true);
  };

  const totalPages = Math.ceil(totalRecord / itemsPerPage);

  const handleMassDelete = async () => {
    console.log("handleMassDelete called", { selectedEnquiries, isAllSelectedAcrossPages });
    if (selectedEnquiries.length === 0 && !isAllSelectedAcrossPages) {
      console.warn("No enquiries selected for deletion");
      return;
    }

    setAlertConfig({
      show: true,
      type: 'confirm',
      title: 'Confirm Delete',
      message: `Are you sure you want to delete ${isAllSelectedAcrossPages ? totalRecord : selectedEnquiries.length} enquiries?`,
      onConfirm: async () => {
        console.log("Delete confirmed");
        setAlertConfig(prev => ({ ...prev, show: false }));
        setIsLoading(true);
        try {
          const hashedIds = isAllSelectedAcrossPages ? "" : selectedEnquiries.join(',');
          const payload = {
            Token: TokenId || "-2295521862261168",
            Details: {
              EntityType: "Enquiry",
              MassOperationName: "Delete",
              Scheduledatetimelead: new Date().toISOString().split('T')[0],
              UserId: Number(userId || "34594"),
              HashedIds: hashedIds
            }
          };

          console.log("Hitting mass delete API with payload:", payload);
          const response = await fetch(`${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.MASS_DELETE_ENQUIRY}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          if (response.ok) {
            console.log("Delete successful");
            setAlertConfig({
              show: true,
              type: 'success',
              title: 'Success',
              message: 'Enquiries deleted successfully!',
              onConfirm: () => {
                setAlertConfig(prev => ({ ...prev, show: false }));
                setSelectedEnquiries([]);
                setIsAllSelectedAcrossPages(false);
                setMassOperation('');
                fecthEnquiry(0, false);
                fetchStats();
              }
            });
          } else {
            console.error("Delete failed", response.status);
            setAlertConfig({
              show: true,
              type: 'error',
              title: 'Error',
              message: 'Failed to delete enquiries. Please try again.',
              onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
            });
          }
        } catch (error) {
          console.error("Error deleting enquiries:", error);
          setAlertConfig({
            show: true,
            type: 'error',
            title: 'Error',
            message: 'A network error occurred.',
            onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
          });
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleMassAction = () => {
    console.log("handleMassAction called with operation:", massOperation);
    if (!massOperation) return;

    if (massOperation === 'update') {
      setActiveOperationMode('update');
      setShowMassOperationPanel(true);
    } else if (massOperation === 'delete') {
      handleMassDelete();
    } else if (massOperation === 'marketing_sequence') {
      setActiveOperationMode('marketing_sequence');
      setShowMassOperationPanel(true);
    } else if (massOperation === 'add_followup') {
      setActiveOperationMode('add_followup');
      setShowMassOperationPanel(true);
    } else if (massOperation === 'mail') {
      setActiveOperationMode('mail');
      setShowMassOperationPanel(true);
    } else if (massOperation === 'sms') {
      setActiveOperationMode('sms');
      setShowMassOperationPanel(true);
    } else if (massOperation === 'international_sms') {
      setActiveOperationMode('international_sms');
      setShowMassOperationPanel(true);
    } else if (massOperation === 'add_tag') {
      setActiveOperationMode('add_tag');
      setShowMassOperationPanel(true);
    } else if (massOperation === 'remove_tag') {
      setActiveOperationMode('remove_tag');
      setShowMassOperationPanel(true);
    } else if (massOperation === 'add_call_list') {
      setActiveOperationMode('add_call_list');
      setShowMassOperationPanel(true);
    } else if (massOperation === 'delete') {
      handleMassDelete();
    }
  };

  const massPanel = (selectedEnquiries.length > 0 || isAllSelectedAcrossPages) ? (
    <div className="flex items-center gap-4 w-full px-4 h-full">
      <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100">
        <span className="text-blue-700 font-bold text-sm">
          {isAllSelectedAcrossPages ? (totalRecord - excludedEnquiries.length) : selectedEnquiries.length}
        </span>
        <span className="text-blue-600 text-[10px] uppercase font-bold tracking-wider">Selected of {totalRecord}</span>
      </div>

      {!isAllSelectedAcrossPages && selectedEnquiries.length === enquiries.length && totalRecord > enquiries.length && (
        <button
          onClick={() => setIsAllSelectedAcrossPages(true)}
          className="text-blue-600 text-[10px] font-bold uppercase tracking-wider hover:underline px-2"
        >
          Select all {totalRecord}
        </button>
      )}

      {(selectedEnquiries.length > 0 || isAllSelectedAcrossPages) && (
        <button
          onClick={() => {
            setSelectedEnquiries([]);
            setIsAllSelectedAcrossPages(false);
            setExcludedEnquiries([]);
          }}
          className="text-slate-400 hover:text-red-500 text-[10px] font-bold uppercase tracking-wider px-2 border-l border-slate-200"
        >
          Clear
        </button>
      )}

      <div className="flex items-center gap-2">
        <select
          className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer"
          value={massOperation}
          onChange={(e) => setMassOperation(e.target.value)}
        >
          <option value="">--Select Mass Operation--</option>
          {crmOperationPerms?.MassUpdate !== false && <option value="update">Update</option>}
          {crmOperationPerms?.MassDelete !== false && <option value="delete">Delete</option>}
          {crmOperationPerms?.MassBroadcast !== false && <option value="marketing_sequence">Marketing Sequence</option>}
          {crmOperationPerms?.AddFollowup !== false && <option value="add_followup">Add Lead / Followup</option>}
          {crmOperationPerms?.MassEmail !== false && <option value="mail">Send Mail</option>}
          {crmOperationPerms?.MassSMS !== false && <option value="sms">Send SMS</option>}
          {crmOperationPerms?.MassSMS !== false && <option value="international_sms">International SMS</option>}
          <option value="add_tag">Add Tag</option>
          <option value="remove_tag">Remove Tag</option>
          <option value="add_call_list">Add Call List</option>
        </select>
        <button
          onClick={handleMassAction}
          className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold uppercase tracking-wider px-4 py-2 rounded-lg shadow-sm transition-all active:scale-95"
        >
          Go
        </button>
      </div>
    </div>
  ) : null;

  const customSearchDropdown = isCustomSearchExpanded ? (
    <div className="flex items-center gap-1.5 animate-in fade-in slide-in-from-left-2 duration-300 ml-4">
      <div className="relative group">
        <select
          value={selectedCustomSearch}
          onChange={(e) => setSelectedCustomSearch(e.target.value)}
          className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 min-w-[200px] pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all cursor-pointer"
        >
          <option value="">--Select Custom Search Name</option>
          {customSearchOptions.map((opt, idx) => {
            const val = opt.Text || opt.Name || opt.SearchName || (typeof opt === 'string' ? opt : '');
            return (
              <option key={idx} value={val}>
                {val}
              </option>
            );
          })}
        </select>
        <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" />
      </div>

      <button
        onClick={() => {
          if (selectedCustomSearch) {
            setAppliedCustomSearchName(selectedCustomSearch);
          }
        }}
        disabled={!selectedCustomSearch}
        className={`p-1.5 rounded-lg border transition-all ${selectedCustomSearch ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700 shadow-sm' : 'bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed'}`}
        title="Apply Custom Search"
      >
        <Icon icon="ri:send-plane-2-line" fontSize={18} />
      </button>

      <button
        onClick={() => {
          setIsCustomSearchExpanded(false);
          setSelectedCustomSearch('');
          setAppliedCustomSearchName('');
        }}
        className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-red-50 hover:text-red-500 hover:border-red-100 transition-all text-slate-400"
        title="Clear and Close"
      >
        <X size={16} />
      </button>
    </div>
  ) : null;

  const customMenuItems = crmOperationPerms?.Search !== false ? (
    <>
      <button
        className={`menu-action-btn ${isCustomSearchExpanded ? 'active' : ''}`}
        onClick={() => setIsCustomSearchExpanded(!isCustomSearchExpanded)}
        title="Custom Search"
      >
        <Icon icon="mdi:filter-cog-outline" fontSize={22} />
      </button>
      <div className="menu-divider"></div>
    </>
  ) : null;

  return (
    <div className="enquiries-modern-layout flex flex-col h-screen overflow-hidden relative">


      {/* Premium Header Integration */}
      <SubHeader
        pageName="Enquiries"
        pageCode="PAG10002"
        title={
          <div className="flex items-center gap-4">
            <span>Enquiries</span>
            {isCustomSearchExpanded && (
              <div className="custom-search-header-container animate-in fade-in slide-in-from-left-2 duration-300">
                <div className="relative group flex items-center gap-1.5">
                  <div className="relative">
                    <select
                      value={selectedCustomSearch}
                      onChange={(e) => setSelectedCustomSearch(e.target.value)}
                      className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] font-semibold text-slate-700 w-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all cursor-pointer h-8 shadow-sm"
                      style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', backgroundImage: 'none' }}
                    >
                      <option value="">Select Custom Search Name</option>
                      {customSearchOptions.map((opt, idx) => {
                        const val = opt.CriteriaName || opt.Text || opt.Name || opt.SearchName || (typeof opt === 'string' ? opt : '');
                        return (
                          <option key={idx} value={val}>
                            {val}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <button
                    onClick={() => {
                      if (selectedCustomSearch) {
                        setAppliedCustomSearchName(selectedCustomSearch);
                      }
                    }}
                    disabled={!selectedCustomSearch}
                    className={`p-1.5 rounded-lg border transition-all h-8 flex items-center justify-center w-8 ${selectedCustomSearch ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700 shadow-sm' : 'bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed'}`}
                    title="Apply Custom Search"
                  >
                    <Icon icon="ri:send-plane-2-line" fontSize={16} />
                  </button>

                  <button
                    onClick={() => {
                      setIsCustomSearchExpanded(false);
                      setSelectedCustomSearch('');
                      setAppliedCustomSearchName('');
                    }}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-red-50 hover:text-red-500 hover:border-red-100 transition-all text-slate-400 h-8 flex items-center justify-center w-8"
                    title="Clear and Close"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        }
        searchQuery={searchText}
        setSearchQuery={setSearchText}
        onSearchClick={crmOperationPerms?.Search !== false ? () => {
          setStartIndex(0);
          fecthEnquiry(0, false);
        } : null}
        searchPlaceholder="Search enquiries..."
        customMenuItems={customMenuItems}
        centerContent={hasDuplicates && viewMode !== 'calendar' ? (
          <div className="duplicate-warning-banner">
            <Icon icon="ri:error-warning-fill" className="warning-icon" />
            <span>You have duplicate enquiries in your list. </span>
            {crmOperationPerms?.MergeLead !== false && (
              <button
                className="merge-link-btn"
                onClick={() => setShowDuplicateConfirmation(true)}
              >
                Merge to Eliminate Duplicates
              </button>
            )}
          </div>
        ) : null}
        viewMode={viewMode}
        setViewMode={setViewMode}
        showGrid={true}
        showTable={true}
        showCalendar={true}
        totalCount={totalRecord}
        onAddClick={crmOperationPerms?.Add !== false ? () => {
          fetchTokenForSaveEdit();
          setShowAddEnquiry(true);
        } : null}
        onColumnsClick={() => setShowColumnSearch(!showColumnSearch)}
        showAdd={crmOperationPerms?.Add !== false}
        showAddInMenu={false}
        showColumns={true}
        showSettings={true}
        showExport={crmOperationPerms?.Export !== false}
        onExportClick={crmOperationPerms?.Export !== false ? handleExportClick : null}
        showImport={crmOperationPerms?.Import !== false}
        onImportClick={crmOperationPerms?.Import !== false ? () => setShowImport(true) : null}
        showRefresh={true}
        onRefreshClick={handleRefresh}
        showExpandableSearch={crmOperationPerms?.Search !== false}
        isSearchExpanded={isSearchExpanded}
        setIsSearchExpanded={setIsSearchExpanded}
        showAdvanceFilter={false}
        showFilter={false}
        onFilterClick={crmOperationPerms?.Search !== false ? () => setIsSidebarFilterOpen(true) : null}
        filterCount={advancedFilters?.rows?.length || 0}
        massActionPanel={massPanel}
        beforeAddContent={
          <button
            className={`subheader-action-btn ${showCustomFields ? 'active' : ''}`}
            onClick={() => setShowCustomFields(!showCustomFields)}
            title="Custom Fields"
          >
            <Icon icon="mdi:wrench" fontSize={20} />
          </button>
        }
      />

      <EnquiryFilterSidebar
        isOpen={isSidebarFilterOpen}
        onOpen={() => setIsSidebarFilterOpen(true)}
        onClose={() => setIsSidebarFilterOpen(false)}
        mainFields={enquiryFilterFields}
        onApply={(filters) => {
          setAdvancedFilters(filters || { logic: 'ALL', rows: [] });
          setStartIndex(0);
          setIsSidebarFilterOpen(false);
          fecthEnquiry(0, false, filters || { logic: 'ALL', rows: [] });
        }}
      />

      {/* Column Visibility Panel (Floating) */}
      <div className="relative">
        {showColumnSearch && (
          <div className="absolute right-[68px] top-4 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-[120] max-h-[400px] flex flex-col overflow-hidden animate-slide-in">
            <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Visible Columns</span>
              <button onClick={() => setShowColumnSearch(false)} className="text-slate-400 hover:text-red-500">
                <X size={14} />
              </button>
            </div>
            <div className="overflow-y-auto p-2 scrollbar-thin">
              {Object.keys(visibleColumns).map(key => {
                if (key === 'leadNo' || key === 'status') return null;
                
                let displayLabel = key.replace(/([A-Z])/g, ' $1').trim();
                if (key.startsWith('custom_')) {
                  const field = customFieldsList.find(f => f.key === key);
                  displayLabel = field ? field.label : key.replace('custom_', '');
                }
                
                if (!displayLabel || displayLabel.toLowerCase() === 'undefined') return null;
                
                return (
                  <button
                    key={key}
                    onClick={() => handleColumnToggle(key)}
                    className={`w-full p-2.5 text-left text-xs rounded-lg flex items-center justify-between transition ${visibleColumns[key] ? 'bg-blue-50 text-blue-700 font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    <span className="capitalize">{displayLabel}</span>
                    {visibleColumns[key] && <span className="w-1.5 h-1.5 bg-blue-600 rounded-full" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <PremiumModuleWrapper
        moduleName="Enquiries"
        customCards={statsData}
        showCards={viewMode !== 'calendar'}
        showTable={false}
        onStatusClick={(key) => {
          setIsDashboardFiltered(false);
          if (key === 'All' || key === 'ResetAll') {
            handleRefresh();
          } else if ((activeFilter === 'StillOpen' ? 'Open' : activeFilter) === key) {
            setActiveFilter('All');
          } else {
            setActiveFilter(key);
          }
        }}
        activeFilters={[activeFilter === 'StillOpen' ? 'Open' : activeFilter]}
      >
        {/* Dashboard filter indicator */}
        {!showLeadDetails && isDashboardFiltered && (
          <div style={{ padding: '8px 24px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#1e40af' }}>
              <Icon icon="lucide:filter" fontSize={14} />
              <span>Filtered by Dashboard: {dashboardFilterLabel}</span>
              <button
                onClick={() => { setIsDashboardFiltered(false); setActiveFilter('All'); }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(30, 64, 175, 0.1)', border: 'none', cursor: 'pointer', color: '#1e40af', padding: 0 }}
              >
                <Icon icon="lucide:plus" fontSize={14} style={{ transform: 'rotate(45deg)' }} />
              </button>
            </div>
          </div>
        )}

        {/* Unified Loader for both initial load and filtered updates */}
        {isLoading && (viewMode === 'table' || ((enquiries.length === 0 || isInitialLoad) && viewMode !== 'grid' && viewMode !== 'calendar')) && (
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 500,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.3s ease',
            backdropFilter: 'blur(2px)'
          }}>
            <Loader message={(isInitialLoad || enquiries.length === 0) ? "Loading enquiries..." : "Updating list..."} />
          </div>
        )}

        <div className="flex flex-1 overflow-hidden" style={{ minHeight: 0, position: 'relative' }}>
          {!isLoading && !isInitialLoad && enquiries.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center bg-white m-4 rounded-2xl border border-slate-100 shadow-sm">
              <img src="/nodata.gif" alt="No records found" className="w-80 h-auto opacity-80" />
              <div className="text-slate-400 font-bold uppercase tracking-widest text-[10px] mt-2">No record(s) found</div>
            </div>
          ) : viewMode === 'table' ? (
            <div className="flex-1 overflow-hidden">
              {showLeadDetails ? (
                <LeadDetails
                  leadId={selectedLead}
                  onBack={() => setShowLeadDetails(false)}
                  maskObject={maskObject}
                  isHiddenField={isHiddenField}
                />
              ) : (
                <EnquiryTable
                  selectedEnquiries={selectedEnquiries}
                  setSelectedEnquiries={setSelectedEnquiries}
                  isAllSelectedAcrossPages={isAllSelectedAcrossPages}
                  setIsAllSelectedAcrossPages={setIsAllSelectedAcrossPages}
                  excludedEnquiries={excludedEnquiries}
                  setExcludedEnquiries={setExcludedEnquiries}
                  enquiries={filteredEnquiries.map(e => maskObject(e))}
                  selectedLead={selectedLead}
                  visibleColumns={visibleColumns}
                  setVisibleColumns={setVisibleColumns}
                  onSelectEnquiry={(enq) => {
                    setSelectedLead(enq.EnquiryId);
                    setSelectedEnquiry(enq);
                  }}
                  onShowDetails={(enq) => {
                    fetchTokenForSaveEdit();
                    setSelectedLead(enq.EnquiryId);
                    setSelectedEnquiry(enq);
                    setShowLeadDetails(true);
                  }}
                  searchText={searchText}
                  onSearchChange={setSearchText}
                  onSearch={searchTText}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                  totalRecord={totalRecord}
                  sortConfig={sortConfig}
                  setSortConfig={setSortConfig}
                  customFieldsList={customFieldsList}
                  itemsPerPage={itemsPerPage}
                  setItemsPerPage={setItemsPerPage}
                  onPageChange={(page) => {
                    const newStart = (page - 1) * itemsPerPage;
                    setStartIndex(newStart);
                    fecthEnquiry(newStart, false);
                  }}
                  onPdfClick={(enq) => {
                    setSelectedEnquiry(enq);
                    setShowPdfPanel(true);
                  }}
                  onInitiateCall={handleInitiateCall}
                />
              )}
            </div>
          ) : viewMode === 'calendar' ? (
            <div className="flex-1 overflow-hidden bg-slate-50/50">
              {showLeadDetails && selectedEnquiry ? (
                <LeadDetails
                  saveEditToken={saveEditToken}
                  leadId={selectedEnquiry.EnquiryId}
                  isopen={selectedEnquiry.IsOpen || selectedEnquiry.Isopen || selectedEnquiry.isopen}
                  onBack={() => setShowLeadDetails(false)}
                />
              ) : (
                <div className="h-full p-6 overflow-hidden">
                  <div className="h-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                    <DynamicCalendar
                      events={mappedEvents}
                      onEventClick={(event) => {
                        fetchTokenForSaveEdit();
                        setSelectedLead(event.original.EnquiryId);
                        setSelectedEnquiry(event.original);
                        setShowLeadDetails(true);
                      }}
                      entityLabel="Enquiry"
                      eventRenderer={(event, view) => {
                        const enq = event.original;
                        const isOpen = String(enq?.IsOpen).toLowerCase() === 'true' || enq?.IsOpen === true;
                        const accentColor = isOpen ? '#00cfe8' : '#f59e0b';
                        const bgColor = isOpen ? '#ecfeff' : '#fffbeb';
                        const ownerName = enq?.OwnerName || enq?.User_login || '-';
                        const ownerInitial = ownerName !== '-' ? ownerName.replace(/^[^a-zA-Z0-9]+/, '').charAt(0)?.toUpperCase() || '?' : '?';
                        const personName = enq?.PersonName || 'Unknown';
                        const personInitial = personName.replace(/^[^a-zA-Z0-9]+/, '').charAt(0)?.toUpperCase() || '?';
                        if (view === 'month') {
                          const personAvatar = getAvatarStyle(personName);
                          return (
                            <div
                              className="group/event"
                              onClick={(e) => {
                                e.stopPropagation();
                                fetchTokenForSaveEdit();
                                setSelectedLead(enq.EnquiryId);
                                setSelectedEnquiry(enq);
                                setShowLeadDetails(true);
                              }}
                              style={{
                                background: bgColor,
                                borderRadius: '6px',
                                padding: '6px 10px',
                                cursor: 'pointer',
                                margin: '0',
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                border: `1px solid ${accentColor}`,
                                borderLeft: `4px solid ${accentColor}`,
                                height: 'auto',
                                minHeight: '32px'
                              }}
                            >
                              <div
                                style={{
                                  width: '18px', height: '18px', borderRadius: '50%',
                                  background: personAvatar.bg, color: personAvatar.text,
                                  border: `1px solid ${personAvatar.border}`,
                                  fontSize: '9px', fontWeight: 800,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  flexShrink: 0
                                }}
                              >
                                {getInitials(personName)}
                              </div>
                              <div style={{ fontSize: '11px', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {personName}
                              </div>
                            </div>
                          );
                        }
                        // Week view — clean card
                        const personAvatar = getAvatarStyle(personName);
                        return (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchTokenForSaveEdit();
                              setSelectedLead(enq.EnquiryId);
                              setSelectedEnquiry(enq);
                              setShowLeadDetails(true);
                            }}
                            style={{
                              background: bgColor,
                              border: `1px solid ${accentColor}`,
                              borderLeft: `4px solid ${accentColor}`,
                              borderRadius: '10px',
                              padding: '12px',
                              cursor: 'pointer',
                              margin: '0 0 8px 0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              boxSizing: 'border-box'
                            }}
                          >
                            <div
                              style={{
                                width: '36px', height: '36px', borderRadius: '50%',
                                background: personAvatar.bg, color: personAvatar.text,
                                border: `2px solid white`,
                                boxShadow: '0 0 0 1px #e2e8f0',
                                fontSize: '13px', fontWeight: 800,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0
                              }}
                            >
                              {getInitials(personName)}
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {personName}
                              </div>
                            </div>
                          </div>
                        );
                      }}
                      headerLegend={
                        <div className="flex gap-4 items-center">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#00cfe8' }}></span>
                            Open
                          </div>
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#f59e0b' }}></span>
                            Lead
                          </div>
                        </div>
                      }
                      statusConfig={{
                        open: '#00cfe8',
                        lead: '#f59e0b'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            showLeadDetails && selectedEnquiry ? (
              <LeadDetails
                saveEditToken={saveEditToken}
                leadId={selectedEnquiry.EnquiryId}
                isopen={selectedEnquiry.IsOpen || selectedEnquiry.Isopen || selectedEnquiry.isopen}
                onBack={() => setShowLeadDetails(false)}
              />
            ) : (
              <GridMainPanelLayout
                type="enquiry"
                selectedEnquiries={selectedEnquiries}
                setSelectedEnquiries={setSelectedEnquiries}
                isAllSelectedAcrossPages={isAllSelectedAcrossPages}
                setIsAllSelectedAcrossPages={setIsAllSelectedAcrossPages}
                excludedEnquiries={excludedEnquiries}
                setExcludedEnquiries={setExcludedEnquiries}
                data={filteredEnquiries.map(e => maskObject(e)).map(enq => ({
                  id: enq.EnquiryId,
                  title: enq.PersonName,
                  name: enq.PersonName,
                  personName: enq.PersonName,
                  enquiryId: enq.EnquiryId,
                  date: enq.CreatedDate,
                  phone: (
                    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                      <button
                        className="hover:scale-110 transition-transform p-1 rounded-full hover:bg-gray-100 flex items-center justify-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedIdForCall(enq.EnquiryId);
                          setIsScheduleCallOpen(true);
                        }}
                        title="Schedule Call"
                      >
                        <Icon icon="material-symbols:add-call-outline" className="text-slate-400 hover:text-blue-600" width={18} />
                      </button>
                      <span
                        className="ph-name-link truncate font-semibold"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInitiateCall(enq, e);
                        }}
                        title="Initiate Call"
                      >
                        {enq.CsvMobileNo}
                      </span>
                    </div>
                  ),
                  email: enq.CsvEmailId,
                  _statusClass: (String(enq.IsOpen).toLowerCase() === 'true' || enq.IsOpen === true) ? 'badge-type-open' : 'badge-type-lead',
                  _badgeText: (String(enq.IsOpen).toLowerCase() === 'true' || enq.IsOpen === true) ? 'OPEN' : 'LEAD',
                  _original: enq
                }))}
                getCompletionBadge={(item) => (
                  <span className={`premium-badge-dark ${item._statusClass}`}>
                    {item._badgeText}
                  </span>
                )}
                loading={isLoading}
                totalRecords={totalRecord}
                currentPage={Math.floor(startIndex / itemsPerPage) + 1}
                totalPages={totalPages}
                onPageChange={(page) => {
                  const newStart = (page - 1) * itemsPerPage;
                  setStartIndex(newStart);
                  fecthEnquiry(newStart, false);
                }}
                selectedItem={selectedEnquiry ? { id: selectedEnquiry.EnquiryId, _original: selectedEnquiry } : null}
                onPdfClick={handlePdfIconClick}
                onSelectItem={(item) => {
                  setSelectedLead(item.id);
                  setSelectedEnquiry(item._original);
                }}
                getInitials={getInitials}
                formatDate={formatDate}
                renderDetailContent={renderEnquiryDetailContent}
                onViewLead={(id) => {
                  fetchTokenForSaveEdit();
                  setSelectedLead(id);
                  const rawEnq = enquiries.find(e => e.EnquiryId === id);
                  if (rawEnq) setSelectedEnquiry(rawEnq);
                  setShowLeadDetails(true);
                }}
                onInitiateCall={handleInitiateCall}
              />
            )
          )}
        </div>
      </PremiumModuleWrapper>

      {/* Add Enquiry Drawer Overlay */}
      {showAddEnquiry && (
        <div className="add-enquiry-drawer-overlay" onClick={() => setShowAddEnquiry(false)}>
          <div className="add-enquiry-drawer" onClick={(e) => e.stopPropagation()}>
            <AddEnquiryForm
              saveEditToken={saveEditToken}
              onBack={() => setShowAddEnquiry(false)}
              onSuccess={() => {
                setShowAddEnquiry(false);
                // Reset to page 0 with newest-first sort so the new entry appears at top in both views
                setStartIndex(0);
                setSortConfig({ key: 'CreatedDate', direction: 'desc' });
                fecthEnquiry(0, false);
                fetchStats();
              }}
            />
          </div>
        </div>
      )}

      {/* Mass Operation Panel */}
      <Panels
        isOpen={showMassOperationPanel}
        onClose={() => setShowMassOperationPanel(false)}
        title={activeOperationMode.replace('_', ' ').toUpperCase()}
        backTitle="Enquiry"
        width={window.innerWidth < 1024 ? '95%' : '800px'}
      >
        <EnquiryMassoperation
          mode={activeOperationMode}
          selectedEnquiries={selectedEnquiries}
          isAllSelectedAcrossPages={isAllSelectedAcrossPages}
          totalRecord={totalRecord}
          onBack={() => setShowMassOperationPanel(false)}
          onSuccess={() => {
            setShowMassOperationPanel(false);
            setSelectedEnquiries([]);
            setIsAllSelectedAcrossPages(false);
            setStartIndex(0);
            setSortConfig({ key: 'CreatedDate', direction: 'desc' });
            fecthEnquiry(0, false);
            fetchStats();
          }}
        />
      </Panels>

      {alertConfig.show && (
        <Alert
          show={true}
          type={alertConfig.type}
          title={alertConfig.title}
          message={alertConfig.message}
          onConfirm={alertConfig.onConfirm}
          onCancel={() => setAlertConfig(prev => ({ ...prev, show: false }))}
          onClose={() => setAlertConfig(prev => ({ ...prev, show: false }))}
        />
      )}

      {showPdfPanel && (
        <PDFExportOverlay
          isOpen={showPdfPanel}
          onClose={() => setShowPdfPanel(false)}
          enquiry={selectedEnquiry}
          SERVICE_AZURE_URL_DEVE_1={SERVICE_AZURE_URL_DEVE_1}
          TokenId={TokenId}
        />
      )}

      {showCallWidget && callWidgetData && createPortal(
        <CallWidget
          show={showCallWidget}
          onClose={() => setShowCallWidget(false)}
          toMobileNo={callWidgetData.toMobileNo}
          toEmail={callWidgetData.toEmail}
          extraContactInfo={callWidgetData.extraContactInfo}
          entityId={callWidgetData.entityId}
          entityName={callWidgetData.entityName}
          personName={callWidgetData.personName}
          triggerRect={callWidgetData.triggerRect}
          pageCode="PAG10372#PAG10002"
        />,
        document.body
      )}

      {isScheduleCallOpen && (
        <Enquiryoperations
          isOpen={isScheduleCallOpen}
          onClose={() => {
            setIsScheduleCallOpen(false);
            setSelectedIdForCall(null);
          }}
          leadId={selectedIdForCall}
        />
      )}

      {/* Duplicate Elimination Confirmation Modal */}
      {showDuplicateConfirmation && (
        <div className="duplicate-modal-overlay">
          <div className="duplicate-modal-card animate-pop">
            <div className="modal-inner-border">
              <h3 className="modal-message">Are You Sure want to Eliminate Duplicates</h3>
              <div className="modal-actions">
                <button className="modal-btn-yes" onClick={() => {
                  setShowDuplicateConfirmation(false);
                  setShowDuplicateSuccess(true);
                }}>Yes</button>
                <button className="modal-btn-no" onClick={() => setShowDuplicateConfirmation(false)}>No</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Elimination Success Modal */}
      {showDuplicateSuccess && (
        <div className="duplicate-modal-overlay">
          <div className="duplicate-modal-card animate-pop success-variant">
            <div className="modal-inner-border bg-blue-50">
              <h2 className="modal-title-congrat">Congratulation! your Duplicate Elimination process is in progress.</h2>
              <p className="modal-subtitle">After merge operation you need to refresh your enquiry list to get updated Data.</p>
              <div className="modal-footer-single">
                <button className="modal-btn-close" onClick={() => {
                  setShowDuplicateSuccess(false);
                  navigate('/MergeDuplicate');
                }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enquiry Import Modal */}
      {showImport && (
        <EnquiryImport
          onClose={() => setShowImport(false)}
          onSuccess={() => {
            setShowImport(false);
            fecthEnquiry(0, false);
            fetchStats();
          }}
        />
      )}

      {/* Custom Fields Modal */}
      {showCustomFields && (
        <div style={{ zIndex: 9999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'white', display: 'flex', flexDirection: 'column' }}>
          <EnquiryCustomField onClose={() => setShowCustomFields(false)} />
        </div>
      )}

    </div>
  );
};

export default Enquiries;
